import { useCallback, useEffect, useState } from 'react';
import {
  Timestamp,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  AuditAction,
  type CancellationReason,
  type Currency,
  type Period,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import {
  directMonthlyRevenue,
  parseSubscriptionDoc,
  reportSubscriptionDrops,
  type SubscriptionDrop,
} from '@/features/dashboard-finance/subscriptionRevenue';
import { isReportExcludedTenant } from '@/features/tenants/testTenantFilter';
import { suggestReason } from './reasonSuggestion';

const TAG = 'admin/churn/useChurnedTenants';
const BRT_OFFSET_MS = 3 * 60 * 60 * 1000;
const D = 24 * 60 * 60 * 1000;

export type ChurnedTenantRow = {
  tenantId: string;
  tenantName: string;
  countryCode: string;
  currency: Currency | null;
  priceCents: number;
  usdEqCents: number;
  suspendedAt: number;
  lifetimeDays: number | null;
  labelsLast30d: number | null;
  reason: CancellationReason | null;
  suggestedReason: CancellationReason;
  auditId: string;
};

export type ChurnSummary = {
  totalRows: number;
  revenueLostUsdCents: number;
  topReason: CancellationReason | null;
  topReasonCount: number;
};

export type ChurnState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; rows: ChurnedTenantRow[]; summary: ChurnSummary };

function brtRangeUtcMs(period: Period): { startUtcMs: number; endUtcMs: number } {
  const startBrt = Date.parse(`${period.from}T00:00:00Z`);
  const endBrt = Date.parse(`${period.to}T00:00:00Z`) + 24 * 60 * 60 * 1000;
  return { startUtcMs: startBrt + BRT_OFFSET_MS, endUtcMs: endBrt + BRT_OFFSET_MS };
}

/**
 * Hook do dashboard `/dashboard/churn`.
 *
 * Origem: sem issue — commit b9c02cd45, 2026-05-04 (rota /dashboard/churn).
 *
 * Contrato: `period` (datas BRT) → `{ state, refetch }`. Combina:
 *  - audit (action=TENANT_SUSPENDED) no range para descobrir
 *    cancelamentos.
 *  - `tenants/{id}/billing/subscription` de cada tenant (qualquer status —
 *    ele já foi suspenso) → preço = receita DIRETA mensal que ele levou
 *    embora (`directMonthlyRevenue` com `anyStatus`); cortesia, demo,
 *    parceiro e test mode saem com moeda `null` e 0, porque não eram receita.
 *  - tenant doc para name/country/createdAt.
 *  - labelsLast30d: hoje sempre `null` (heurística sem fonte barata).
 *  - reason já gravado em audit.cancellationReason (se categorizado).
 *  - tenant de TESTE (#1008) não é churn: o cadastrado sai antes das
 *    leituras, o sandbox (`livemode: false`) depois de ler a assinatura
 *    (`isReportExcludedTenant`). Nem linha, nem receita perdida, nem motivo.
 * Retorna sumário com receita perdida (USD-eq) e motivo mais comum. Erro →
 * `ERROR` + `reportNonFatal('admin_churn_load_failed')`; doc de assinatura
 * fora do schema → `admin_subscription_doc_dropped` (uma vez por carga).
 *
 * Usado por: `ChurnDashboardPage.tsx` (web `apps/admin`, super-admin); o
 * `refetch` vira o `onChanged` do `ChurnTable` (recarrega após categorizar).
 *
 * Arquitetura: régua de receita única em
 * `dashboard-finance/subscriptionRevenue.ts` — a mesma do churn do
 * `aggregateDailyMetrics` (`directMonthlyRevenue(..., { anyStatus: true })`).
 * Só leitura (a escrita do motivo é da `ChurnTable`, pela callable
 * `categorizeChurnReason`). A
 * collection-group query `audit` é autorizada por
 * `match /{path=**}/audit/{auditId}` de `firebase/firestore.rules`
 * (`isSuperAdmin()`) e usa o índice composto CG `audit` `action ASC, at ASC`
 * de `firebase/firestore.indexes.json`. O CG casa `/audit/*` raiz e
 * `audit/billing/items/*`; o filtro por `action` deixa só suspensões.
 *
 * Dados (anonimizado):
 *   audit/{auditId}
 *     { "action": "TENANT_SUSPENDED", "targetId": "tenant-1",
 *       "at": <Timestamp>, "cancellationReason": "USER_REQUEST" }
 *     — `cancellationReason` só existe depois de categorizado; valor fora
 *       dos quatro conhecidos vira `null`. `at` numérico (legado) é aceito;
 *       ausente vira 0.
 *   tenants/{tenantId}/billing/subscription — valor em centavos por ciclo
 *     (`total_amount_cents ?? amount`), lido em qualquer status.
 *   tenants/{tenantId} → `name`, `countryCode`, `createdAt` (ms).
 * Custo: duas leituras por tenant cancelado (N+1), aceitável no volume de
 * cancelamentos de um período.
 *
 * Dicas: o comentário dentro do `load` sobre `labelsLast30d` ("aproxima via
 * aggregations daily") não descreve o código, que grava sempre `null` —
 * registrado na #926.
 *
 * Falhas conhecidas:
 *  - 2026-05-19 → 2026-10-03 (#795, ADM-4): lia `billing/current` +
 *    `priceCents` (órfão) — preço "—" e receita perdida US$ 0,00 em todas as
 *    linhas, sem aviso.
 *
 *  - até 2026-10-19 (#1008): toda suspensão de tenant sandbox/teste entrava
 *    como cancelamento.
 *
 * Issues: #795 (ADM-4), #926 (achados), #1008 (tenant de teste fora).
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-4) passa a ler `billing/subscription`;
 *    falha de carga vai ao PostHog (antes só `console.error`).
 *  - 2026-10-19 20:57 +0200 — (#1008) exclui tenant de teste.
 */
export function useChurnedTenants(period: Period): {
  state: ChurnState;
  refetch: () => void;
} {
  const [state, setState] = useState<ChurnState>({ kind: 'LOADING' });
  const [reloadTick, setReloadTick] = useState(0);

  const periodFrom = period.from;
  const periodTo = period.to;

  const refetch = useCallback((): void => {
    setReloadTick((n) => n + 1);
  }, []);

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });
    const { startUtcMs, endUtcMs } = brtRangeUtcMs({
      preset: 'CUSTOM',
      from: periodFrom,
      to: periodTo,
    });

    async function load(): Promise<void> {
      const drops: SubscriptionDrop[] = [];
      try {
        const auditSnap = await getDocs(
          query(
            collectionGroup(firestore, 'audit'),
            where('action', '==', AuditAction.TENANT_SUSPENDED),
            where('at', '>=', Timestamp.fromMillis(startUtcMs)),
            where('at', '<', Timestamp.fromMillis(endUtcMs)),
          ),
        );
        const interim: {
          auditId: string;
          tenantId: string;
          suspendedAt: number;
          reason: CancellationReason | null;
        }[] = [];
        for (const d of auditSnap.docs) {
          const tid: unknown = d.get('targetId');
          if (typeof tid !== 'string') continue;
          // #1008: tenant de teste cadastrado sai antes das duas leituras.
          if (isReportExcludedTenant(tid)) continue;
          const at: unknown = d.get('at');
          const ms =
            at instanceof Timestamp ? at.toMillis() : typeof at === 'number' ? at : 0;
          const reasonRaw: unknown = d.get('cancellationReason');
          const reason =
            typeof reasonRaw === 'string' &&
            (reasonRaw === 'USER_REQUEST' ||
              reasonRaw === 'PAYMENT_FAILED' ||
              reasonRaw === 'INACTIVITY' ||
              reasonRaw === 'UNKNOWN')
              ? reasonRaw
              : null;
          interim.push({ auditId: d.id, tenantId: tid, suspendedAt: ms, reason });
        }

        const maybeRows = await Promise.all(
          interim.map(async (entry): Promise<ChurnedTenantRow | null> => {
            // Bug corrigido em 2026-10-03 14:12 +0200 (#795, ADM-4): lia
            // `billing` com `__name__ == 'current'` — doc órfão desde
            // 2026-05-19 (`e9692f87e`); toda linha saía sem preço e a receita
            // perdida somava 0. A fonte é `billing/subscription`, em qualquer
            // status (o tenant já foi suspenso), pela régua do churn das
            // Functions.
            const subSnap = await getDoc(
              doc(firestore, 'tenants', entry.tenantId, 'billing', 'subscription'),
            );
            const parsed = subSnap.exists()
              ? parseSubscriptionDoc(subSnap.ref.path, subSnap.data())
              : null;
            if (parsed && !parsed.ok) drops.push(parsed.drop);
            // #1008: suspensão de sandbox (`livemode: false`) não é churn.
            if (parsed?.ok && isReportExcludedTenant(entry.tenantId, parsed.fields)) return null;
            const lost = parsed?.ok ? directMonthlyRevenue(parsed.fields, { anyStatus: true }) : null;
            const priceCents = lost?.monthlyCents ?? 0;
            const currency: Currency | null = lost?.currency ?? null;
            const usd = lost?.usdEqCents ?? 0;

            const tenantSnap = await getDocs(
              query(
                collection(firestore, 'tenants'),
                where('__name__', '==', entry.tenantId),
              ),
            );
            const tenantData = tenantSnap.docs[0]?.data() ?? {};
            const name = typeof tenantData.name === 'string' ? tenantData.name : entry.tenantId;
            const country = typeof tenantData.countryCode === 'string' ? tenantData.countryCode : '';
            const createdAt = typeof tenantData.createdAt === 'number' ? tenantData.createdAt : null;
            const lifetime =
              createdAt !== null
                ? Math.max(0, Math.floor((entry.suspendedAt - createdAt) / D))
                : null;

            // labelsLast30d: aproxima via aggregations daily (mais barato que
            // collectionGroup days). Aceita não ter dados pré-tracking → null.
            const labelsLast30d = null as number | null;

            const suggested = suggestReason({
              lifetimeDays: lifetime,
              labelsLast30d,
              billingFailedAtMs: null,
              suspendedAtMs: entry.suspendedAt,
            });

            return {
              tenantId: entry.tenantId,
              tenantName: name,
              countryCode: country,
              currency,
              priceCents,
              usdEqCents: usd,
              suspendedAt: entry.suspendedAt,
              lifetimeDays: lifetime,
              labelsLast30d,
              reason: entry.reason,
              suggestedReason: suggested,
              auditId: entry.auditId,
            };
          }),
        );
        const rows = maybeRows.filter((r): r is ChurnedTenantRow => r !== null);

        rows.sort((a, b) => b.suspendedAt - a.suspendedAt);

        const reasonCounts = new Map<CancellationReason, number>();
        let revenueLost = 0;
        for (const r of rows) {
          revenueLost += r.usdEqCents;
          if (r.reason) reasonCounts.set(r.reason, (reasonCounts.get(r.reason) ?? 0) + 1);
        }
        let topReason: CancellationReason | null = null;
        let topReasonCount = 0;
        for (const [reason, count] of reasonCounts.entries()) {
          if (count > topReasonCount) {
            topReason = reason;
            topReasonCount = count;
          }
        }

        if (guard.cancelled) return;
        reportSubscriptionDrops('churn', drops);
        setState({
          kind: 'READY',
          rows,
          summary: {
            totalRows: rows.length,
            revenueLostUsdCents: revenueLost,
            topReason,
            topReasonCount,
          },
        });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        reportNonFatal('admin_churn_load_failed', serializeError(err));
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [periodFrom, periodTo, reloadTick]);

  return { state, refetch };
}
